(() => {
  if (window.__pmHomeSlice02Loaded) return;
  window.__pmHomeSlice02Loaded = true;

  const mobileQuery = window.matchMedia('(max-width: 915px)');

  class PmSliceCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pmReady === 'true') return;

      this.viewport = this.querySelector('[data-pm-carousel-viewport]');
      this.track = this.querySelector('[data-pm-carousel-track]');
      this.originalSlides = [...this.querySelectorAll('[data-pm-carousel-slide]')];
      this.previousButton = this.querySelector('[data-pm-carousel-previous]');
      this.nextButton = this.querySelector('[data-pm-carousel-next]');
      this.dots = [...this.querySelectorAll('[data-pm-carousel-dot]')];
      this.status = this.querySelector('[data-pm-carousel-status]');
      this.logicalIndex = 0;
      this.physicalIndex = 0;
      this.visibleCount = 1;
      this.animating = false;
      this.pendingSnap = null;
      this.loopRequested = this.dataset.loop === 'true';
      this.pointerId = null;
      this.pointerStart = null;
      this.dragging = false;

      if (!this.viewport || !this.track || this.originalSlides.length === 0) return;

      this.handlePrevious = () => this.move(-1);
      this.handleNext = () => this.move(1);
      this.handleTransitionEnd = (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishMove();
      };
      this.handleViewportKeydown = (event) => this.onViewportKeydown(event);
      this.handlePointerDown = (event) => this.onPointerDown(event);
      this.handlePointerUp = (event) => this.onPointerUp(event);
      this.handleDragStart = (event) => event.preventDefault();
      this.handleMediaChange = () => this.rebuild();

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.track.addEventListener('transitionend', this.handleTransitionEnd);
      this.viewport.addEventListener('keydown', this.handleViewportKeydown);
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.viewport.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.viewport.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
      this.viewport.addEventListener('lostpointercapture', this.handlePointerCancel, { passive: true });
      this.viewport.addEventListener('dragstart', this.handleDragStart);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', this.handleMediaChange);
      } else {
        mobileQuery.addListener(this.handleMediaChange);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueHeightUpdate());
        this.originalSlides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      this.dataset.pmReady = 'true';
      this.rebuild();
    }

    disconnectedCallback() {
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.track?.removeEventListener('transitionend', this.handleTransitionEnd);
      this.viewport?.removeEventListener('keydown', this.handleViewportKeydown);
      this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
      this.viewport?.removeEventListener('lostpointercapture', this.handlePointerCancel);
      this.viewport?.removeEventListener('dragstart', this.handleDragStart);
      this.resizeObserver?.disconnect();
      clearTimeout(this.transitionTimer);
      cancelAnimationFrame(this.heightFrame);

      if (typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        mobileQuery.removeListener(this.handleMediaChange);
      }
    }

    requestedVisibleCount() {
      const value = mobileQuery.matches ? this.dataset.mobileVisible : this.dataset.desktopVisible;
      return Math.max(1, Math.min(this.originalSlides.length, Number.parseInt(value || '1', 10)));
    }

    rebuild() {
      if (!this.track) return;

      this.track.querySelectorAll('[data-pm-carousel-clone]').forEach((clone) => clone.remove());
      this.visibleCount = this.requestedVisibleCount();
      this.effectiveLoop = this.loopRequested && this.originalSlides.length > this.visibleCount;
      this.logicalIndex = Math.max(0, Math.min(this.originalSlides.length - 1, this.logicalIndex));
      this.style.setProperty('--pm-carousel-visible', String(this.visibleCount));

      if (this.effectiveLoop) {
        const before = this.originalSlides.slice(-this.visibleCount).map((slide) => this.makeClone(slide));
        const after = this.originalSlides.slice(0, this.visibleCount).map((slide) => this.makeClone(slide));
        const beforeFragment = document.createDocumentFragment();
        before.forEach((clone) => beforeFragment.appendChild(clone));
        this.track.insertBefore(beforeFragment, this.originalSlides[0]);
        after.forEach((clone) => this.track.appendChild(clone));
        this.baseIndex = this.visibleCount;
      } else {
        this.baseIndex = 0;
        this.logicalIndex = Math.min(this.logicalIndex, this.maximumIndex());
      }

      this.slides = [...this.track.children];
      this.physicalIndex = this.baseIndex + this.logicalIndex;
      this.pendingSnap = null;
      this.animating = false;
      this.setTrackPosition(false);
      this.updateState();
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.pmCarouselClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    maximumIndex() {
      return this.effectiveLoop
        ? this.originalSlides.length - 1
        : Math.max(0, this.originalSlides.length - this.visibleCount);
    }

    move(direction) {
      if (this.animating || this.originalSlides.length <= this.visibleCount) return;

      if (!this.effectiveLoop) {
        this.goTo(this.logicalIndex + direction);
        return;
      }

      this.animating = true;
      if (direction > 0) {
        this.physicalIndex += 1;
        if (this.logicalIndex === this.originalSlides.length - 1) {
          this.logicalIndex = 0;
          this.pendingSnap = this.baseIndex;
        } else {
          this.logicalIndex += 1;
        }
      } else {
        this.physicalIndex -= 1;
        if (this.logicalIndex === 0) {
          this.logicalIndex = this.originalSlides.length - 1;
          this.pendingSnap = this.baseIndex + this.logicalIndex;
        } else {
          this.logicalIndex -= 1;
        }
      }

      this.setTrackPosition(true);
      this.updateState();
      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => this.finishMove(), 360);
    }

    goTo(index) {
      if (this.animating) return;
      const safeIndex = Math.max(0, Math.min(this.maximumIndex(), index));
      if (safeIndex === this.logicalIndex) return;

      this.logicalIndex = safeIndex;
      this.physicalIndex = this.baseIndex + safeIndex;
      this.pendingSnap = null;
      this.animating = true;
      this.setTrackPosition(true);
      this.updateState();
      clearTimeout(this.transitionTimer);
      this.transitionTimer = setTimeout(() => this.finishMove(), 360);
    }

    finishMove() {
      if (!this.animating && this.pendingSnap === null) return;
      clearTimeout(this.transitionTimer);

      if (this.pendingSnap !== null) {
        this.physicalIndex = this.pendingSnap;
        this.pendingSnap = null;
        this.setTrackPosition(false);
      }

      this.animating = false;
      this.queueHeightUpdate();
    }

    setTrackPosition(animate) {
      this.track.dataset.pmNoTransition = String(!animate);
      const percentage = -(this.physicalIndex * (100 / this.visibleCount));
      this.track.style.transform = `translate3d(${percentage}%, 0, 0)`;

      if (!animate) {
        requestAnimationFrame(() => {
          if (this.track) this.track.dataset.pmNoTransition = 'false';
        });
      }
    }

    updateState() {
      const maximum = this.maximumIndex();

      this.previousButton?.toggleAttribute('disabled', !this.effectiveLoop && this.logicalIndex === 0);
      this.nextButton?.toggleAttribute('disabled', !this.effectiveLoop && this.logicalIndex === maximum);

      this.dots.forEach((dot, index) => {
        const usable = index <= maximum;
        dot.hidden = !usable;
        dot.disabled = !usable;
        dot.setAttribute('aria-current', String(index === this.logicalIndex));
      });

      this.originalSlides.forEach((slide, index) => {
        let isVisible = false;
        for (let offset = 0; offset < this.visibleCount; offset += 1) {
          const visibleIndex = this.effectiveLoop
            ? (this.logicalIndex + offset) % this.originalSlides.length
            : this.logicalIndex + offset;
          if (visibleIndex === index) isVisible = true;
        }
        slide.setAttribute('aria-hidden', String(!isVisible));
      });

      if (this.status) {
        const label = this.classList.contains('pm-review-carousel') ? 'Review' : 'Health risk';
        this.status.textContent = `${label} ${this.logicalIndex + 1} of ${this.originalSlides.length}`;
      }

      this.queueHeightUpdate();
    }

    queueHeightUpdate() {
      cancelAnimationFrame(this.heightFrame);
      this.heightFrame = requestAnimationFrame(() => {
        if (!this.viewport || !this.slides?.length) return;
        const visibleSlides = this.slides.slice(this.physicalIndex, this.physicalIndex + this.visibleCount);
        const height = Math.max(0, ...visibleSlides.map((slide) => slide.scrollHeight));
        if (height > 0) this.viewport.style.height = `${height}px`;
      });
    }

    onViewportKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.maximumIndex());
      }
    }

    onPointerDown(event) {
      if (!event.isPrimary || this.pointerId !== null || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.pointerId = event.pointerId;
      this.pointerStart = { x: event.clientX, y: event.clientY };
      this.dragging = false;
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId || !this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
        this.cancelPointer(event.pointerId);
        return;
      }
      this.dragging = Math.abs(deltaX) > 8;
    }

    cancelPointer(pointerId = this.pointerId) {
      if (pointerId !== null && this.viewport?.hasPointerCapture?.(pointerId)) {
        try { this.viewport.releasePointerCapture(pointerId); } catch (_) {}
      }
      this.pointerId = null;
      this.pointerStart = null;
      this.dragging = false;
    }

    onPointerUp(event) {
      if (this.pointerId !== event.pointerId || !this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.cancelPointer(event.pointerId);

      if (Math.abs(deltaX) >= 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.move(deltaX < 0 ? 1 : -1);
      }
    }

    handlePointerCancel(event) {
      if (this.pointerId === event.pointerId) this.cancelPointer(event.pointerId);
    }

    refresh() {
      this.rebuild();
    }
  }

  if (!customElements.get('pm-slice-carousel')) {
    customElements.define('pm-slice-carousel', PmSliceCarousel);
  }

  const marqueeObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.inView = String(entry.isIntersecting);
        });
      }, { rootMargin: '100px 0px' })
    : null;

  function initializeComparisons(root) {
    root.querySelectorAll('[data-pm-comparison]').forEach((comparison) => {
      if (comparison.dataset.pmReady === 'true') return;
      const range = comparison.querySelector('[data-pm-comparison-range]');
      if (!range) return;

      const update = () => comparison.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', update);
      update();
      comparison.dataset.pmReady = 'true';
    });
  }

  function initializeVideos(root) {
    root.querySelectorAll('[data-pm-video-player]').forEach((player) => {
      if (player.dataset.pmReady === 'true') return;
      const video = player.querySelector('[data-pm-video]');
      const playButton = player.querySelector('[data-pm-video-play]');
      if (!video || !playButton) return;

      playButton.addEventListener('click', () => {
        const playAttempt = video.play();
        if (playAttempt && typeof playAttempt.then === 'function') {
          playAttempt.then(() => { player.dataset.playing = 'true'; }).catch(() => {});
        } else {
          player.dataset.playing = 'true';
        }
      });
      video.addEventListener('play', () => { player.dataset.playing = 'true'; });
      player.dataset.pmReady = 'true';
    });
  }

  function setActiveTab(tabList, activeButton, moveFocus = false) {
    const buttons = [...tabList.querySelectorAll('[role="tab"][data-pm-tab-target]')];
    const targetId = activeButton.dataset.pmTabTarget;

    buttons.forEach((button) => {
      const active = button === activeButton;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });

    const panels = [...document.querySelectorAll('.trigger_tab_custom')];
    panels.forEach((panel) => {
      const active = panel.id === targetId;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
      panel.setAttribute('role', 'tabpanel');
      if (active) panel.setAttribute('aria-labelledby', activeButton.id);
    });

    const activePanel = document.getElementById(targetId);
    activePanel?.querySelectorAll('pm-slice-carousel').forEach((carousel) => carousel.refresh());
    activePanel?.dispatchEvent(new CustomEvent('pm:panel-shown', { bubbles: true }));
    tabList.dispatchEvent(new CustomEvent('pm:tab-change', { bubbles: true, detail: { targetId } }));
    if (moveFocus) activeButton.focus();
  }

  function initializeTabs(root) {
    root.querySelectorAll('[data-pm-tabs]').forEach((tabList) => {
      if (tabList.dataset.pmReady === 'true') return;
      const buttons = [...tabList.querySelectorAll('[role="tab"][data-pm-tab-target]')];
      if (buttons.length === 0) return;

      buttons.forEach((button) => {
        button.addEventListener('click', () => setActiveTab(tabList, button));
        button.addEventListener('keydown', (event) => {
          const current = buttons.indexOf(button);
          let target = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = buttons[(current + 1) % buttons.length];
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = buttons[(current - 1 + buttons.length) % buttons.length];
          if (event.key === 'Home') target = buttons[0];
          if (event.key === 'End') target = buttons[buttons.length - 1];
          if (!target) return;
          event.preventDefault();
          setActiveTab(tabList, target, true);
        });
      });

      const selected = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];
      setActiveTab(tabList, selected);
      tabList.dataset.pmReady = 'true';
    });
  }

  function initializeMarquees(root) {
    root.querySelectorAll('[data-pm-marquee]').forEach((marquee) => {
      if (marquee.dataset.pmObserved === 'true') return;
      if (marqueeObserver) {
        marqueeObserver.observe(marquee);
      } else {
        marquee.dataset.inView = 'true';
      }
      marquee.dataset.pmObserved = 'true';
    });
  }

  function initialize(root = document) {
    initializeComparisons(root);
    initializeVideos(root);
    initializeTabs(root);
    initializeMarquees(root);
  }

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    document.querySelectorAll('[data-pm-tabs]').forEach((tabList) => {
      const selected = tabList.querySelector('[role="tab"][aria-selected="true"]');
      if (selected) setActiveTab(tabList, selected);
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!marqueeObserver) return;
    event.target.querySelectorAll('[data-pm-marquee]').forEach((marquee) => marqueeObserver.unobserve(marquee));
  });
})();


